import './JourneyErrorsPopover.scss';

import React, { useEffect, useRef, useState, type JSX } from 'react';

import { getIcon } from '@flybits/design-system';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { getBackendJourneyErrors } from 'helpers/error.helper';
import { JourneyErrorAction } from 'store/journey/journey.type';
import useDraggablePopover from './useDraggablePopover';

const MAIN_CLASS = 'journey-errors-popover';
const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  TITLE: `${MAIN_CLASS}__title`,
  ACTIONS: `${MAIN_CLASS}__actions`,
  COUNT: `${MAIN_CLASS}__count`,
  CLOSE: `${MAIN_CLASS}__close`,
  LIST: `${MAIN_CLASS}__list`,
  ITEM: `${MAIN_CLASS}__item`,
  BULLET: `${MAIN_CLASS}__bullet`,
};

const ACTION_TITLE: Record<JourneyErrorAction, string> = {
  save: 'Unable to save experience',
  launch: 'Unable to launch experience',
  schedule: 'Unable to schedule experience',
  activate: 'Unable to activate experience',
  deactivate: 'Unable to deactivate experience',
};

function JourneyErrorsPopover(): JSX.Element | null {
  const { offset, onDragHandleMouseDown } = useDraggablePopover();

  const richerror = useSelector((state) => state.te.journey.richerror);
  const topLevelErrors = useSelector((state) => {
    const beErrors = Array.isArray(state.te.journey.richerror?.errors) ? state.te.journey.richerror.errors : [];
    return getBackendJourneyErrors(beErrors, state).topLevelErrors;
  });
  const action = useSelector((state) => state.te.journey.errorContext?.action ?? 'save');

  const [isDismissed, setIsDismissed] = useState(false);
  const prevRichError = useRef(richerror);

  // Re-open on every new failure: any fresh richerror object (set by the thunks on a failed
  // save/launch/schedule/activate) clears a prior manual dismissal.
  useEffect(() => {
    if (richerror && richerror !== prevRichError.current) {
      setIsDismissed(false);
    }
    prevRichError.current = richerror;
  }, [richerror]);

  if (isDismissed || topLevelErrors.length === 0) return null;

  const count = topLevelErrors.length;
  const countLabel = `${count} ${count === 1 ? 'problem' : 'problems'}`;

  return (
    <div
      className={MAIN_CLASS}
      role="alert"
      style={{ transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)` }}
    >
      <div className={CLASSES.HEADER} onPointerDown={onDragHandleMouseDown}>
        <span className={CLASSES.TITLE}>{ACTION_TITLE[action]}</span>
        <div className={CLASSES.ACTIONS}>
          <span className={CLASSES.COUNT}>
            {countLabel}
            {getIcon('error', { fill: '#b32013' })}
          </span>
          <button className={CLASSES.CLOSE} aria-label="dismiss errors" onClick={() => setIsDismissed(true)}>
            {getIcon('clear', {})}
          </button>
        </div>
      </div>
      <ul className={CLASSES.LIST}>
        {topLevelErrors.map((error) => (
          <li className={CLASSES.ITEM} key={error.stateIdx}>
            <span className={CLASSES.BULLET}>{getIcon('error', { fill: '#b32013' })}</span>
            <span>{error.err}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default JourneyErrorsPopover;
